import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Server,
  Activity,
  XCircle,
  TrendingUp,
  FileWarning,
  Eye,
  Play,
  Terminal,
  Cloud,
  Shield,
  Key,
  Database,
  Network,
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  Undo2,
  Zap,
  Wand2,
  BookOpen,
  Download,
  FileCode,
  FileText,
  GitPullRequest,
  Star,
  Lightbulb,
  Pencil,
  History,
  MessageSquare,
  X,
} from 'lucide-react';
import { useTheme } from '@/components/theme-provider';

// Import tribal knowledge source icons
import SlackIcon from '../assets/icons/slack-color.png';
import NotionLogoLight from '../assets/icons/notion-logo-no-background.png';
import NotionLogoDark from '../assets/icons/notion-logo-white.png';
import { ReactComponent as ConfluenceIcon } from '../assets/icons/confluence.svg';
import { ReactComponent as JiraIcon } from '../assets/icons/jira.svg';
import LinearLogoLight from '../assets/icons/linear.png';
import LinearLogoDark from '../assets/icons/linear-dark-mode.png';
import { ReactComponent as GleanIcon } from '../assets/icons/glean.svg';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ModeToggle } from '@/components/mode-toggle';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import awsLogoDark from '../assets/icons/aws-dark.svg';
import awsLogoLight from '../assets/icons/aws.svg';
import OCILogo from '../assets/icons/oci.png';
import { apiClient, CloudIncidentSummary, CloudAPIFix, ApplyFixResponse, RollbackFixResponse, ValidateCloudFixResponse, TerraformFix, IaCRepoConfig, TribalKnowledgeSnippet, OCICLIFix, CLIPlaceholder } from '../api/client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getStatusLabel } from '../lib/incidents';
import { parseFormattedText } from '@/lib/textFormatting';
import * as Diff from 'diff';
import { CloudIncidentChat } from '@/components/CloudIncidentChat';
import { MobileMenuButton } from '@/components/MobileMenuButton';

// Diff helper for code fix display
const createDiff = (oldText: string, newText: string): Array<{line: string, type: 'unchanged' | 'removed' | 'added', lineNumber?: number}> => {
  const changes = Diff.diffLines(oldText, newText);
  const result: Array<{line: string, type: 'unchanged' | 'removed' | 'added', lineNumber?: number}> = [];
  let oldLineNumber = 1;
  let newLineNumber = 1;
  changes.forEach(change => {
    const lines = change.value.split('\n');
    if (lines[lines.length - 1] === '') lines.pop();
    lines.forEach(line => {
      if (change.removed) {
        result.push({ line, type: 'removed', lineNumber: oldLineNumber++ });
      } else if (change.added) {
        result.push({ line, type: 'added', lineNumber: newLineNumber++ });
      } else {
        result.push({ line, type: 'unchanged', lineNumber: oldLineNumber });
        oldLineNumber++;
        newLineNumber++;
      }
    });
  });
  return result;
};

// Format timestamp to full datetime in UTC
function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}


// Format ISO timestamp to time
function formatTimeFromISO(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

// Get initials from email address for avatar display
function getInitials(email: string): string {
  const name = email.split('@')[0] || '';
  const parts = name.split(/[._-]/).filter(p => p.length > 0);
  if (parts.length >= 2 && parts[0].length > 0 && parts[1].length > 0) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (name.slice(0, 2) || '??').toUpperCase();
}

// Get severity icon
function getSeverityIcon(severity: string, className?: string) {
  const classes = className || 'h-5 w-5';
  switch (severity) {
    case 'critical':
      return <AlertTriangle className={cn(classes, 'text-red-500')} />;
    case 'high':
      return <AlertCircle className={cn(classes, 'text-orange-500')} />;
    case 'medium':
      return <AlertCircle className={cn(classes, 'text-yellow-500')} />;
    case 'low':
      return <AlertCircle className={cn(classes, 'text-blue-500')} />;
    default:
      return <Activity className={cn(classes, 'text-gray-500')} />;
  }
}

// Get severity badge variant
function getSeverityBadgeVariant(severity: string): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    default:
      return 'secondary';
  }
}

// Check if a CLI command contains placeholders - matches {{placeholder_name}}
function hasPlaceholders(command: string): boolean {
  if (!command) return false;
  // Use a simple includes check first for performance, then verify with regex
  if (!command.includes('{{')) return false;
  // Create a fresh regex each time to avoid lastIndex issues with global flag
  return /\{\{[^}]+\}\}/.test(command);
}

// Extract placeholder names from a CLI command
function extractPlaceholders(command: string): string[] {
  if (!command) return [];
  // Create a fresh regex each time
  const matches = command.match(/\{\{([^}]+)\}\}/g);
  if (!matches) return [];
  // Use Array.from to avoid downlevelIteration issues with Set
  const uniqueNames = Array.from(new Set(matches.map(m => m.replace(/\{\{|\}\}/g, ''))));
  return uniqueNames;
}

// Render CLI command with highlighted placeholders
function renderCommandWithPlaceholders(
  command: string,
  onPlaceholderClick?: (name: string) => void
): React.ReactNode {
  if (!command) return command;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  // Create a fresh regex instance for this call
  const regex = /\{\{([^}]+)\}\}/g;
  
  while ((match = regex.exec(command)) !== null) {
    // Add text before the placeholder
    if (match.index > lastIndex) {
      parts.push(command.slice(lastIndex, match.index));
    }
    
    // Add the placeholder as a highlighted, clickable span (without curly braces, using cyan/teal color)
    const placeholderName = match[1];
    const handleClick = onPlaceholderClick ? (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onPlaceholderClick(placeholderName);
    } : undefined;
    
    parts.push(
      <span
        key={`placeholder-${match.index}`}
        role={onPlaceholderClick ? "button" : undefined}
        tabIndex={onPlaceholderClick ? 0 : undefined}
        className={cn(
          "bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 px-1.5 py-0.5 rounded font-medium border border-zinc-400 dark:border-zinc-600 inline-block",
          onPlaceholderClick && "cursor-pointer hover:bg-zinc-300 dark:hover:bg-zinc-700 hover:border-zinc-500 dark:hover:border-zinc-500 transition-colors select-none"
        )}
        onClick={handleClick}
        onKeyDown={onPlaceholderClick ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPlaceholderClick(placeholderName);
          }
        } : undefined}
        title={onPlaceholderClick ? `Click to edit: ${placeholderName}` : placeholderName}
      >
        {placeholderName}
      </span>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < command.length) {
    parts.push(command.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : command;
}

// Strip placeholder curly braces from a string for display
function stripPlaceholderBraces(text: string): string {
  return text.replace(/\{\{([^}]+)\}\}/g, '$1');
}

// Get status icon
function getStatusIcon(status: string) {
  switch (status) {
    case 'open':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'acknowledged':
      return <Eye className="h-4 w-4 text-yellow-500" />;
    case 'investigating':
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    case 'resolved':
      return <FileWarning className="h-4 w-4 text-amber-500" />; // Analysis Complete
    case 'remediation_applied':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'resolved_confirmed':
      return <CheckCircle className="h-4 w-4 text-foreground" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
}

// Get status badge variant
function getStatusBadgeVariant(status: string): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (status) {
    case 'open':
      return 'destructive';
    case 'acknowledged':
      return 'secondary'; // Acknowledged - yellow
    case 'investigating':
      return 'default';
    case 'resolved':
      return 'secondary'; // Analysis Complete - amber
    case 'remediation_applied':
      return 'default';
    case 'resolved_confirmed':
      return 'outline'; // Final resolved - green
    default:
      return 'secondary';
  }
}

// Format incident type for display
// Handles patterns like "CLOUDTRAILError" -> "CloudTrail Error", "S3PolicyChange" -> "S3 Policy Change"
function formatIncidentType(type: string): string {
  // Common AWS service abbreviations that should stay uppercase or have special formatting
  const serviceAbbreviations: Record<string, string> = {
    'CLOUDTRAIL': 'CloudTrail',
    'CLOUDWATCH': 'CloudWatch',
    'CLOUDFRONT': 'CloudFront',
    'CLOUDFORMATION': 'CloudFormation',
    'DYNAMODB': 'DynamoDB',
    'ELASTICACHE': 'ElastiCache',
    'ELASTICSEARCH': 'Elasticsearch',
    'OPENSEARCH': 'OpenSearch',
    'APIGATEWAY': 'API Gateway',
    'STEPFUNCTIONS': 'Step Functions',
    'SECRETSMANAGER': 'Secrets Manager',
    'EVENTBRIDGE': 'EventBridge',
    'CODEPIPELINE': 'CodePipeline',
    'CODEBUILD': 'CodeBuild',
    'CODEDEPLOY': 'CodeDeploy',
    'CODECOMMIT': 'CodeCommit',
    'SAGEMAKER': 'SageMaker',
    'GUARDDUTY': 'GuardDuty',
    'SECURITYHUB': 'Security Hub',
    'ACCESSANALYZER': 'Access Analyzer',
    'APPRUNNER': 'App Runner',
    'S3': 'S3',
    'EC2': 'EC2',
    'IAM': 'IAM',
    'VPC': 'VPC',
    'ECS': 'ECS',
    'EKS': 'EKS',
    'RDS': 'RDS',
    'SNS': 'SNS',
    'SQS': 'SQS',
    'KMS': 'KMS',
    'WAF': 'WAF',
    'ACM': 'ACM',
    'EMR': 'EMR',
    'SSM': 'SSM',
    'ELB': 'ELB',
    'ALB': 'ALB',
    'NLB': 'NLB',
    'API': 'API',
    'AWS': 'AWS',
  };

  // Check if type matches <ALLCAPS>Error or <ALLCAPS>Something pattern
  const allCapsMatch = type.match(/^([A-Z0-9]+)(Error|Warning|Alert|Issue|Anomaly|Change|Modification|Access|Violation)?$/);
  if (allCapsMatch) {
    const servicePart = allCapsMatch[1];
    const suffixPart = allCapsMatch[2] || '';
    
    // Look up the service name
    const formattedService = serviceAbbreviations[servicePart] || 
      // Default: convert to title case with spaces between words
      servicePart.charAt(0) + servicePart.slice(1).toLowerCase();
    
    if (suffixPart) {
      return `${formattedService} ${suffixPart}`;
    }
    return formattedService;
  }

  // Handle PascalCase/camelCase by inserting spaces before capital letters
  // But keep abbreviations like S3, EC2, IAM together
  let result = type
    .replace(/([A-Z]{2,})([A-Z][a-z])/g, '$1 $2') // Handle transitions like "IAMPolicy" -> "IAM Policy"
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Handle camelCase transitions
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'); // Handle "HTTPError" -> "HTTP Error"
  
  return result;
}

// Get cloud incident type label
function getCloudIncidentTypeLabel(type: string): string {
  switch (type) {
    // IAM incidents
    case 'IAMCompromise':
      return 'IAM Compromise';
    case 'IAMPolicyViolation':
      return 'IAM Policy Violation';
    case 'IAMRoleAbuse':
      return 'IAM Role Abuse';
    case 'UnauthorizedAccess':
      return 'Unauthorized Access';
    case 'CredentialLeak':
      return 'Credential Leak';
    case 'PrivilegeEscalation':
      return 'Privilege Escalation';
    // S3 incidents
    case 'S3DataExfiltration':
      return 'S3 Data Exfiltration';
    case 'S3PolicyModification':
      return 'S3 Policy Modification';
    case 'S3PublicAccess':
      return 'S3 Public Access';
    case 'S3EncryptionDisabled':
      return 'S3 Encryption Disabled';
    // EC2 incidents
    case 'EC2Anomaly':
      return 'EC2 Anomaly';
    case 'EC2InstanceCompromise':
      return 'EC2 Instance Compromise';
    case 'EC2UnauthorizedAccess':
      return 'EC2 Unauthorized Access';
    // Network incidents
    case 'NetworkTrafficAnomaly':
      return 'Network Traffic Anomaly';
    case 'SecurityGroupModification':
      return 'Security Group Modification';
    case 'VPCModification':
      return 'VPC Modification';
    case 'NetworkTrafficBlocked':
      return 'Network Traffic Blocked';
    // Service health
    case 'ServiceHealthDegradation':
      return 'Service Health Degradation';
    case 'APIThrottling':
      return 'API Throttling';
    case 'ResourceExhaustion':
      return 'Resource Exhaustion';
    // Config compliance
    case 'ConfigComplianceViolation':
      return 'Config Compliance Violation';
    case 'SecurityHubFinding':
      return 'Security Hub Finding';
    default:
      // Use the smart formatter for unrecognized types
      return formatIncidentType(type);
  }
}

// Get cloud incident type icon
function getCloudIncidentTypeIcon(type: string) {
  if (type.includes('IAM') || type.includes('Access') || type.includes('Credential')) {
    return <Key className="h-5 w-5 text-muted-foreground" />;
  } else if (type.includes('S3') || type.includes('Storage')) {
    return <Database className="h-5 w-5 text-muted-foreground" />;
  } else if (type.includes('Network') || type.includes('VPC') || type.includes('SecurityGroup')) {
    return <Network className="h-5 w-5 text-muted-foreground" />;
  } else if (type.includes('Security') || type.includes('Compromise')) {
    return <Shield className="h-5 w-5 text-muted-foreground" />;
  } else if (type.includes('EC2') || type.includes('Instance')) {
    return <Server className="h-5 w-5 text-muted-foreground" />;
  } else {
    return <Cloud className="h-5 w-5 text-muted-foreground" />;
  }
}

// Get AWS service icon
function getAWSServiceIcon(serviceName: string) {
  switch (serviceName?.toLowerCase()) {
    case 'iam':
      return <Key className="h-4 w-4 text-muted-foreground" />;
    case 's3':
      return <Database className="h-4 w-4 text-muted-foreground" />;
    case 'ec2':
      return <Server className="h-4 w-4 text-muted-foreground" />;
    case 'vpc':
    case 'securitygroup':
      return <Network className="h-4 w-4 text-muted-foreground" />;
    case 'cloudtrail':
      return <Activity className="h-4 w-4 text-muted-foreground" />;
    default:
      return <Cloud className="h-4 w-4 text-muted-foreground" />;
  }
}

// Get timeline event icon
function getTimelineEventIcon(eventType: string, severity: string) {
  // Monochrome: black in light mode, white in dark mode (theme foreground).
  const cls = 'h-4 w-4 text-foreground';
  switch (eventType) {
    case 'api_call':
      return <Terminal className={cls} />;
    case 'config_change':
      return <TrendingUp className={cls} />;
    case 'metric_anomaly':
      return <Activity className={cls} />;
    case 'alert':
      return <AlertTriangle className={cls} />;
    case 'compliance_violation':
      return <Shield className={cls} />;
    case 'pagerduty_alert':
      return <AlertTriangle className={cls} />;
    default:
      return <Clock className={cls} />;
  }
}

// Get risk level badge variant
function getRiskLevelBadgeVariant(riskLevel: string): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (riskLevel?.toLowerCase()) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    case 'low':
      return 'secondary';
    default:
      return 'outline';
  }
}

// Investigation Summary Display Component
interface InvestigationSummary {
  what_happened?: string;
  how_detected?: string;
  impact_scope?: string;
  contributing_factors?: string[];
  evidence_collected?: string[];
  [key: string]: unknown; // Allow additional fields
}

function InvestigationSummaryDisplay({ summary }: { summary: string | InvestigationSummary }) {
  // Parse if string
  let parsedSummary: InvestigationSummary;
  if (typeof summary === 'string') {
    try {
      parsedSummary = JSON.parse(summary);
    } catch {
      // If parsing fails, show as plain text
      return (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Investigation Summary</h4>
          <p className="text-sm whitespace-pre-wrap">{summary}</p>
        </div>
      );
    }
  } else {
    parsedSummary = summary;
  }

  // Check if it's actually an object with content
  if (!parsedSummary || typeof parsedSummary !== 'object' || Object.keys(parsedSummary).length === 0) {
    return null;
  }

  const sections = [
    { key: 'what_happened', label: 'What Happened', icon: <AlertCircle className="h-4 w-4 text-muted-foreground" /> },
    { key: 'how_detected', label: 'How Detected', icon: <Eye className="h-4 w-4 text-muted-foreground" /> },
    { key: 'impact_scope', label: 'Impact Scope', icon: <TrendingUp className="h-4 w-4 text-muted-foreground" /> },
  ];

  const listSections = [
    { key: 'contributing_factors', label: 'Contributing Factors', icon: <FileWarning className="h-4 w-4 text-muted-foreground" /> },
    { key: 'evidence_collected', label: 'Evidence Collected', icon: <Activity className="h-4 w-4 text-muted-foreground" /> },
  ];

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground">Investigation Summary</h4>
      
      {/* Text sections */}
      <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-3">
        {sections.map(({ key, label, icon }) => {
          const value = parsedSummary[key];
          if (!value) return null;
          return (
            <div key={key} className="bg-muted/50 rounded-lg p-4 border border-border/50">
              <div className="flex items-center gap-2 mb-2">
                {icon}
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
              </div>
              <p className="text-sm">{String(value)}</p>
            </div>
          );
        })}
      </div>

      {/* List sections */}
      <div className="grid gap-4 md:grid-cols-2">
        {listSections.map(({ key, label, icon }) => {
          const value = parsedSummary[key];
          if (!value || !Array.isArray(value) || value.length === 0) return null;
          return (
            <div key={key} className="bg-muted/50 rounded-lg p-4 border border-border/50">
              <div className="flex items-center gap-2 mb-3">
                {icon}
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
              </div>
              <ul className="space-y-2">
                {value.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-muted-foreground mt-1">•</span>
                    <span>{String(item)}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Any additional fields not covered above */}
      {Object.entries(parsedSummary)
        .filter(([key]) => !['what_happened', 'how_detected', 'impact_scope', 'contributing_factors', 'evidence_collected'].includes(key))
        .map(([key, value]) => {
          if (!value) return null;
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return (
            <div key={key} className="bg-muted/50 rounded-lg p-4 border border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4 text-gray-500" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
              </div>
              {Array.isArray(value) ? (
                <ul className="space-y-1">
                  {value.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <span className="text-muted-foreground mt-1">•</span>
                      <span>{String(item)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm">{String(value)}</p>
              )}
            </div>
          );
        })}
    </div>
  );
}

// Tribal Knowledge Source Icon Component
type TribalKnowledgeSourceType = 'slack' | 'notion' | 'confluence' | 'jira' | 'linear' | 'glean';

const TribalKnowledgeSourceIcon: React.FC<{ sourceType: string; className?: string }> = ({ sourceType, className = "h-5 w-5" }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  switch (sourceType as TribalKnowledgeSourceType) {
    case 'slack':
      return <img src={SlackIcon} alt="Slack" className={className} />;
    case 'notion':
      return <img src={isDarkMode ? NotionLogoDark : NotionLogoLight} alt="Notion" className={className} />;
    case 'confluence':
      return <ConfluenceIcon className={className} />;
    case 'jira':
      return <JiraIcon className={className} />;
    case 'linear':
      return <img src={isDarkMode ? LinearLogoDark : LinearLogoLight} alt="Linear" className={className} />;
    case 'glean':
      return <GleanIcon className={className} />;
    default:
      return <FileText className={className} />;
  }
};

export default function CloudIncidentDetail() {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const [incident, setIncident] = useState<CloudIncidentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedFix, setExpandedFix] = useState<number | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [resolvingIncident, setResolvingIncident] = useState(false);
  const [triggeringRCA, setTriggeringRCA] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showChat, setShowChat] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  const [chatMounted, setChatMounted] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  const [applyingFix, setApplyingFix] = useState<number | null>(null);
  const [rollingBackFix, setRollingBackFix] = useState<number | null>(null);
  const [validatingFix, setValidatingFix] = useState<number | null>(null);
  const [fixResult, setFixResult] = useState<{ index: number; result: ApplyFixResponse | RollbackFixResponse; type: 'apply' | 'rollback' } | null>(null);
  const [validationResult, setValidationResult] = useState<{ index: number; result: ValidateCloudFixResponse } | null>(null);
  const [showApplyDialog, setShowApplyDialog] = useState<number | null>(null);
  const [showRollbackDialog, setShowRollbackDialog] = useState<number | null>(null);
  const [showValidateDialog, setShowValidateDialog] = useState<number | null>(null);

  // Terraform/IaC state
  const [terraformFixes, setTerraformFixes] = useState<TerraformFix[]>([]);
  const [iacRepos, setIacRepos] = useState<IaCRepoConfig[]>([]);
  const [loadingTerraformFixes, setLoadingTerraformFixes] = useState(false);
  const [creatingPR, setCreatingPR] = useState<string | null>(null);
  const [expandedTerraformFix, setExpandedTerraformFix] = useState<string | null>(null);
  const [creatingCodeFixPR, setCreatingCodeFixPR] = useState(false);
  const [retryingCodeAnalysis, setRetryingCodeAnalysis] = useState(false);
  const [codeFixPrUrl, setCodeFixPrUrl] = useState<string | null>(null);

  // Tribal Knowledge pagination
  const [tribalKnowledgePage, setTribalKnowledgePage] = useState(1);
  const TRIBAL_KNOWLEDGE_PER_PAGE = 5;

  // Incident history pagination
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_PER_PAGE = 5;

  // Signals pagination
  const [signalsPage, setSignalsPage] = useState(1);
  const SIGNALS_PER_PAGE = 5;

  // OCI connection name lookup
  const [ociConnectionName, setOciConnectionName] = useState<string | null>(null);

  // Placeholder editing state
  const [editingPlaceholders, setEditingPlaceholders] = useState<{ fixIndex: number; placeholders: CLIPlaceholder[] } | null>(null);
  const [savingPlaceholders, setSavingPlaceholders] = useState(false);

  // Fetch incident details
  useEffect(() => {
    async function fetchIncident() {
      if (!incidentId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await apiClient.getCloudIncidentDetails(incidentId);
        setIncident(data);
        setHistoryPage(0);

        // Initialize code fix PR URL from persisted data
        if (data?.rca_result?.code_fix_pr_url) {
          setCodeFixPrUrl(data.rca_result.code_fix_pr_url);
        }

        // Fetch OCI connection name if this is an OCI incident
        if (data.cloud_provider === 'oci' && data.oci_connection_id) {
          try {
            const ociResponse = await apiClient.getOCIConnectionStatus();
            const conn = ociResponse.connections?.find(c => c.id === data.oci_connection_id);
            if (conn?.connection_name) {
              setOciConnectionName(conn.connection_name);
            }
          } catch (ociErr) {
            console.error('Failed to fetch OCI connection name:', ociErr);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch cloud incident:', err);
        setError(err.response?.data?.message || 'Failed to load incident details');
      } finally {
        setLoading(false);
      }
    }

    fetchIncident();
  }, [incidentId]);

  // Fetch IaC repos and Terraform fixes
  useEffect(() => {
    async function fetchIaCData() {
      if (!incidentId) return;

      try {
        // Fetch IaC repos for this tenant
        const reposData = await apiClient.listIaCRepos();
        setIacRepos(reposData.iac_repos || []);

        // Fetch existing Terraform fixes for this incident
        setLoadingTerraformFixes(true);
        const fixesData = await apiClient.getCloudIncidentTerraformFixes(incidentId);
        setTerraformFixes(fixesData.terraform_fixes || []);
      } catch (err) {
        // IaC not configured or other error - silently ignore
        console.log('IaC data not available:', err);
      } finally {
        setLoadingTerraformFixes(false);
      }
    }

    fetchIaCData();
  }, [incidentId]);

  // Auto-refresh when async operations are polling
  useEffect(() => {
    // Check if any fix has async_status === 'polling'
    const hasPollingFix = incident?.rca_result?.api_fixes?.some(
      (fix) => fix.async_status === 'polling'
    );

    if (!hasPollingFix || !incidentId) return;

    // Poll every 10 seconds while async operation is in progress
    const intervalId = setInterval(async () => {
      try {
        const data = await apiClient.getCloudIncidentDetails(incidentId);
        setIncident(data);
        
        // Check if polling is complete
        const stillPolling = data?.rca_result?.api_fixes?.some(
          (fix) => fix.async_status === 'polling'
        );
        if (!stillPolling) {
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Failed to refresh during async polling:', err);
      }
    }, 10000); // 10 second refresh

    return () => clearInterval(intervalId);
  }, [incident?.rca_result?.api_fixes, incidentId]);

  // Copy CLI command to clipboard
  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle resolve incident
  const handleResolveIncident = async () => {
    if (!incidentId) return;

    setResolvingIncident(true);
    try {
      await apiClient.markCloudIncidentResolved(incidentId);
      // Refresh incident data
      const data = await apiClient.getCloudIncidentDetails(incidentId);
      setIncident(data);
      setShowResolveDialog(false);
    } catch (err: any) {
      console.error('Failed to resolve incident:', err);
      setError(err.response?.data?.message || 'Failed to resolve incident');
    } finally {
      setResolvingIncident(false);
    }
  };

  // Handle status change from dropdown
  const handleStatusChange = async (newStatus: string) => {
    if (!incidentId || !incident) return;

    // If changing to resolved_confirmed, show the dialog for confirmation
    if (newStatus === 'resolved_confirmed') {
      setShowResolveDialog(true);
      return;
    }

    setUpdatingStatus(true);
    try {
      await apiClient.updateCloudIncidentStatus(incidentId, newStatus);
      // Refresh incident data
      const data = await apiClient.getCloudIncidentDetails(incidentId);
      setIncident(data);
    } catch (err: any) {
      console.error('Failed to update incident status:', err);
      setError(err.response?.data?.message || 'Failed to update incident status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle trigger RCA
  const handleTriggerRCA = async () => {
    if (!incidentId) return;

    setTriggeringRCA(true);
    try {
      await apiClient.triggerCloudIncidentRCA(incidentId);
      // Refresh incident data
      const data = await apiClient.getCloudIncidentDetails(incidentId);
      setIncident(data);
    } catch (err: any) {
      console.error('Failed to trigger RCA:', err);
      setError(err.response?.data?.message || 'Failed to trigger RCA');
    } finally {
      setTriggeringRCA(false);
    }
  };

  // Refresh incident data
  const refreshIncident = async () => {
    if (!incidentId) return;
    setLoading(true);
    try {
      const data = await apiClient.getCloudIncidentDetails(incidentId);
      setIncident(data);
    } catch (err: any) {
      console.error('Failed to refresh incident:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle apply fix
  const handleApplyFix = async (fixIndex: number) => {
    if (!incidentId) return;

    setApplyingFix(fixIndex);
    setFixResult(null);
    setShowApplyDialog(null);

    try {
      const result = await apiClient.applyCloudIncidentFix(incidentId, fixIndex);
      setFixResult({ index: fixIndex, result, type: 'apply' });
      
      // Refresh incident data to get updated fix status
      const data = await apiClient.getCloudIncidentDetails(incidentId);
      setIncident(data);
    } catch (err: any) {
      console.error('Failed to apply fix:', err);
      setFixResult({
        index: fixIndex,
        result: {
          success: false,
          message: 'Failed to apply fix',
          error: err.response?.data?.message || err.message || 'Unknown error',
        },
        type: 'apply',
      });
    } finally {
      setApplyingFix(null);
    }
  };

  // Handle rollback fix
  const handleRollbackFix = async (fixIndex: number) => {
    if (!incidentId) return;

    setRollingBackFix(fixIndex);
    setFixResult(null);
    setShowRollbackDialog(null);

    try {
      const result = await apiClient.rollbackCloudIncidentFix(incidentId, fixIndex);
      setFixResult({ index: fixIndex, result, type: 'rollback' });
      
      // Refresh incident data to get updated fix status
      const data = await apiClient.getCloudIncidentDetails(incidentId);
      setIncident(data);
    } catch (err: any) {
      console.error('Failed to rollback fix:', err);
      setFixResult({
        index: fixIndex,
        result: {
          success: false,
          message: 'Failed to rollback fix',
          error: err.response?.data?.message || err.message || 'Unknown error',
        },
        type: 'rollback',
      });
    } finally {
      setRollingBackFix(null);
    }
  };

  // Open placeholder editor for a fix
  const handleEditPlaceholders = (fixIndex: number, fix: CloudAPIFix | OCICLIFix) => {
    // Extract placeholders from CLI command if not already defined
    const existingPlaceholders = fix.placeholders || [];
    const commandPlaceholders = extractPlaceholders(fix.cli_command);
    const rollbackPlaceholders = fix.rollback_command ? extractPlaceholders(fix.rollback_command) : [];
    
    // Combine all unique placeholders
    const allNames = [
      ...existingPlaceholders.map(p => p.name),
      ...commandPlaceholders,
      ...rollbackPlaceholders,
    ];
    const allPlaceholderNames = Array.from(new Set(allNames));
    
    // Create placeholder objects with existing values or empty
    const placeholders: CLIPlaceholder[] = allPlaceholderNames.map(name => {
      const existing = existingPlaceholders.find(p => p.name === name);
      return {
        name,
        description: existing?.description || `Value for ${name}`,
        example: existing?.example || '',
        value: existing?.value || '',
      };
    });
    
    setEditingPlaceholders({ fixIndex, placeholders });
  };

  // Handle placeholder value change
  const handlePlaceholderChange = (placeholderName: string, value: string) => {
    if (!editingPlaceholders) return;
    
    setEditingPlaceholders({
      ...editingPlaceholders,
      placeholders: editingPlaceholders.placeholders.map(p =>
        p.name === placeholderName ? { ...p, value } : p
      ),
    });
  };

  // Save placeholder changes
  const handleSavePlaceholders = async () => {
    if (!incidentId || !editingPlaceholders) return;
    
    setSavingPlaceholders(true);
    
    try {
      const result = await apiClient.updateFixPlaceholders(
        incidentId,
        editingPlaceholders.fixIndex,
        editingPlaceholders.placeholders
      );
      
      if (result.success) {
        // Refresh incident data to get updated CLI command
        const data = await apiClient.getCloudIncidentDetails(incidentId);
        setIncident(data);
        setEditingPlaceholders(null);
      } else {
        console.error('Failed to update placeholders:', result.error);
      }
    } catch (err: any) {
      console.error('Failed to update placeholders:', err);
    } finally {
      setSavingPlaceholders(false);
    }
  };

  // Check if all placeholders have values
  const allPlaceholdersFilled = editingPlaceholders
    ? editingPlaceholders.placeholders.every(p => p.value && p.value.trim() !== '')
    : false;

  // Handle validate fix
  const handleValidateFix = async (fixIndex: number) => {
    if (!incidentId) return;

    setValidatingFix(fixIndex);
    setValidationResult(null);
    setShowValidateDialog(null);

    try {
      const result = await apiClient.validateCloudIncidentFix(incidentId, fixIndex);
      setValidationResult({ index: fixIndex, result });
      
      // Refresh incident data to get updated status
      const data = await apiClient.getCloudIncidentDetails(incidentId);
      setIncident(data);
    } catch (err: any) {
      console.error('Failed to validate fix:', err);
      setValidationResult({
        index: fixIndex,
        result: {
          success: false,
          validation_status: 'unknown',
          validation_summary: err.response?.data?.message || err.message || 'Unknown error',
          validation_details: [],
          checked_at: new Date().toISOString(),
          aws_commands_executed: 0,
          error: err.response?.data?.message || err.message || 'Unknown error',
        },
      });
    } finally {
      setValidatingFix(null);
    }
  };

  // Create PR for Terraform fix
  const handleCreateTerraformPR = async (fixId: string) => {
    if (!incidentId) return;

    setCreatingPR(fixId);
    try {
      const result = await apiClient.createTerraformPR(incidentId, fixId);
      if (result.success && result.pr_url) {
        // Update the fix in state with PR info
        setTerraformFixes((prev) =>
          prev.map((f) =>
            f.id === fixId
              ? { ...f, pr_status: 'pr_created', pr_url: result.pr_url }
              : f
          )
        );
        // Open PR in new tab
        window.open(result.pr_url, '_blank');
      } else {
        setError(result.error || 'Failed to create pull request');
      }
    } catch (err: any) {
      console.error('Failed to create PR:', err);
      setError(err.response?.data?.message || 'Failed to create pull request');
    } finally {
      setCreatingPR(null);
    }
  };

  // Get fix status badge
  const getFixStatusBadge = (fix: CloudAPIFix | OCICLIFix) => {
    // Check async status first
    if (fix.async_status) {
      switch (fix.async_status) {
        case 'polling':
          return (
            <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Polling...
            </Badge>
          );
        case 'completed':
          return <Badge variant="default" className="bg-foreground text-background">Completed</Badge>;
        case 'failed':
          return <Badge variant="destructive">Async Failed</Badge>;
        case 'timeout':
          return <Badge variant="destructive">Timed Out</Badge>;
      }
    }
    switch (fix.status) {
      case 'applied':
        return <Badge variant="default" className="bg-foreground text-background">Applied</Badge>;
      case 'rolled_back':
        return <Badge variant="secondary">Rolled Back</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  // Get resource identifier from fix (ARN for AWS, OCID for OCI)
  const getFixResourceIdentifier = (fix: CloudAPIFix | OCICLIFix): string => {
    if ('resource_arn' in fix) {
      return fix.resource_arn;
    }
    return fix.resource_ocid;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/cloud-incidents')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Cloud Incidents
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/cloud-incidents')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Cloud Incidents
        </Button>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Not Found</AlertTitle>
          <AlertDescription>Cloud incident not found.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const hasRCA = incident.rca_result != null;
  const timeline = incident.rca_result?.timeline || [];
  const apiFixes = incident.rca_result?.api_fixes || [];
  const ociCliFixes = incident.rca_result?.oci_cli_fixes || [];
  const isOCIIncident = incident.cloud_provider === 'oci';
  // Use the correct fixes array based on cloud provider
  const currentFixes = isOCIIncident ? ociCliFixes : apiFixes;
  const totalCliFixes = currentFixes.length;

  // Calculate combined affected resources (same logic as in the tab content)
  const detectedResources = incident.affected_resources || [];
  const rcaResources = incident.rca_result?.affected_resources || [];
  const normalizedRcaResources = rcaResources.map((res) => ({
    arn: res.arn || '',
    resource_type: res.resource_type || '',
    resource_id: res.resource_id || res.name || '',
    resource_name: res.name || res.resource_name || '',
    region: res.region || '',
  }));
  const seenArns = new Set(detectedResources.map(r => r.arn));
  const uniqueRcaResources = normalizedRcaResources.filter(r => r.arn && !seenArns.has(r.arn));
  const allAffectedResources = [...detectedResources, ...uniqueRcaResources];

  return (
    <div className="flex flex-col -mx-4 sm:-mx-6 lg:-mx-8 -my-4 overflow-hidden" style={{ height: '100dvh' }}>
      {/* Page Header — fixed at top */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 border-b border-border bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <MobileMenuButton />
            {incident.cloud_provider === "oci" ? <img src={OCILogo} alt="OCI" className="h-6 w-6" /> : <><img src={awsLogoLight} alt="AWS" className="h-6 w-6 dark:hidden" /><img src={awsLogoDark} alt="AWS" className="h-6 w-6 hidden dark:block" /></>}
            <h1 className="text-2xl font-bold truncate">{getCloudIncidentTypeLabel(incident.type)}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            {!hasRCA && incident.status === 'open' && (
              <Button
                variant="default"
                size="sm"
                onClick={handleTriggerRCA}
                disabled={triggeringRCA}
              >
                {triggeringRCA ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                <span className="hidden sm:inline">Trigger RCA</span>
              </Button>
            )}
            {incident.status !== 'resolved_confirmed' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground hidden sm:inline">Status:</span>
                <Select
                  value={incident.status}
                  onValueChange={handleStatusChange}
                  disabled={updatingStatus || resolvingIncident}
                >
                  <SelectTrigger className="w-auto min-w-[180px] h-9 whitespace-nowrap">
                    {updatingStatus ? (
                      <div className="flex items-center">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </div>
                    ) : (
                      <SelectValue placeholder="Select status" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-destructive" />
                        Open
                      </div>
                    </SelectItem>
                    <SelectItem value="acknowledged">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-yellow-500" />
                        Acknowledged
                      </div>
                    </SelectItem>
                    <SelectItem value="investigating">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 text-blue-500" />
                        Investigating
                      </div>
                    </SelectItem>
                    <SelectItem value="resolved">
                      <div className="flex items-center gap-2">
                        <FileWarning className="h-4 w-4 text-amber-500" />
                        Analysis Complete
                      </div>
                    </SelectItem>
                    <SelectItem value="resolved_confirmed">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        Resolved
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              aria-label={showChat ? 'Hide Chat' : 'Show Chat'}
              onClick={() => {
                if (!showChat) setChatMounted(true);
                setShowChat(!showChat);
              }}
              className="flex items-center gap-2"
            >
              {showChat ? (
                <>
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">Hide Chat</span>
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Show Chat</span>
                </>
              )}
            </Button>
            <ModeToggle />
          </div>
        </div>
      </div>

      {/* Content area — main + chat side by side */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
      {/* Main Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 lg:px-8 py-6 min-w-0">
        <div className={cn("space-y-6", showChat ? "md:pr-1" : "")}>

      {/* Incident Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={getStatusBadgeVariant(incident.status)} className="text-sm whitespace-nowrap">
              {getStatusLabel(incident.status)}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={getSeverityBadgeVariant(incident.severity)} className="text-sm">
              <span className="flex items-center gap-1 [&_svg]:!text-current">
                {getSeverityIcon(incident.severity, 'h-4 w-4')}
                {incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1)}
              </span>
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {incident.cloud_provider === 'oci' ? 'OCI Account' : 'AWS Account'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <code className="text-sm bg-muted px-2 py-1 rounded">
              {incident.cloud_provider === 'oci' 
                ? (ociConnectionName || incident.oci_tenancy_ocid?.split('.').pop()?.substring(0, 16) || 'N/A')
                : incident.aws_account_id}
            </code>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Detected</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-sm">{formatDateTime(incident.detected_at)}</span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Resource Owners</CardTitle>
          </CardHeader>
          <CardContent>
            {incident.cloud_resource_owner_emails && incident.cloud_resource_owner_emails.length > 0 ? (
              <div className="flex items-center -space-x-2">
                <TooltipProvider>
                  {incident.cloud_resource_owner_emails.slice(0, 4).map((email, idx) => (
                    <Tooltip key={email}>
                      <TooltipTrigger asChild>
                        <div
                          className="w-8 h-8 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center border-2 border-background font-medium cursor-default"
                          style={{ zIndex: 4 - idx }}
                          aria-label={`Resource owner: ${email}`}
                          role="img"
                        >
                          {getInitials(email)}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{email}</p>
                        <p className="text-xs text-muted-foreground">Resource Owner</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                  {incident.cloud_resource_owner_emails.length > 4 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="w-8 h-8 rounded-full bg-muted text-muted-foreground text-xs flex items-center justify-center border-2 border-background font-medium cursor-default"
                          aria-label={`${incident.cloud_resource_owner_emails.length - 4} more resource owners`}
                          role="img"
                        >
                          +{incident.cloud_resource_owner_emails.length - 4}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {incident.cloud_resource_owner_emails.slice(4).map(email => (
                          <p key={email}>{email}</p>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TooltipProvider>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">No owners assigned</span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Root Cause & Details */}
      {hasRCA && (
        <Card>
          <CardHeader>
            <CardTitle>
              Root Cause Analysis
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {incident.rca_result?.transient && (
                <Badge variant="outline" className="w-fit">
                  Transient Incident
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Root Cause</h4>
              <p className="text-sm whitespace-pre-wrap">{incident.rca_result?.root_cause}</p>
            </div>
            {incident.rca_result?.investigation_summary && (
              <InvestigationSummaryDisplay summary={incident.rca_result.investigation_summary} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs for Signals, Timeline, and Fixes */}
      <Tabs defaultValue="signals" className="space-y-4">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="signals" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Signals ({incident.signals?.length || 0})
          </TabsTrigger>
          {hasRCA && (
            <>
              {timeline.length > 0 && (
                <TabsTrigger value="timeline" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Timeline ({timeline.length})
                </TabsTrigger>
              )}
              {incident.rca_result?.is_application_level_failure && (incident.rca_result?.code_fixes?.length || 0) > 0 && (
                <TabsTrigger value="code-fixes" className="flex items-center gap-2">
                  <FileCode className="h-4 w-4" />
                  Code Fixes ({incident.rca_result.code_fixes?.length || 0})
                </TabsTrigger>
              )}
              {totalCliFixes > 0 && (
                <TabsTrigger value="fixes" className="flex items-center gap-2">
                  <Terminal className="h-4 w-4" />
                  {isOCIIncident ? 'OCI' : 'AWS'} Fixes ({totalCliFixes})
                </TabsTrigger>
              )}
              {iacRepos.length > 0 && terraformFixes.length > 0 && (
                <TabsTrigger value="iac" className="flex items-center gap-2">
                  {incident.cloud_provider === "oci" ? <img src={OCILogo} alt="OCI" className="h-4 w-4" /> : <><img src={awsLogoLight} alt="AWS" className="h-4 w-4 dark:hidden" /><img src={awsLogoDark} alt="AWS" className="h-4 w-4 hidden dark:block" /></>}
                  IaC Fixes ({terraformFixes.length})
                </TabsTrigger>
              )}
              {incident.rca_result?.tribal_knowledge_used && incident.rca_result.tribal_knowledge_used.length > 0 && (
                <TabsTrigger value="tribal-knowledge" className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Tribal Knowledge ({incident.rca_result.tribal_knowledge_used.length})
                </TabsTrigger>
              )}
            </>
          )}
          <TabsTrigger value="resources" className="flex items-center gap-2">
            <Server className="h-4 w-4" />
            Affected Resources ({allAffectedResources.length})
          </TabsTrigger>
          {incident.previous_incidents && incident.previous_incidents.length > 0 && (
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Incident History ({incident.previous_incidents.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Signals Tab */}
        <TabsContent value="signals">
          <Card>
            <CardHeader>
              <CardTitle>Incident Signals</CardTitle>
              <CardDescription>
                {incident.cloud_provider === 'oci' 
                  ? 'OCI events and alerts that triggered this incident detection'
                  : 'AWS events and alerts that triggered this incident detection'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {incident.signals && incident.signals.length > 0 ? (
                <>
                  <div className="space-y-4">
                    {incident.signals
                      .slice((signalsPage - 1) * SIGNALS_PER_PAGE, signalsPage * SIGNALS_PER_PAGE)
                      .map((signal, index) => (
                      <div key={index} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{signal.event_name || signal.signal_type}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={getSeverityBadgeVariant(signal.severity)}>
                              {signal.severity}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatTimeFromISO(signal.timestamp)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{signal.message}</p>
                        {(() => {
                          const meta = typeof signal.metadata === 'string'
                            ? (() => { try { return JSON.parse(signal.metadata); } catch { return null; } })()
                            : signal.metadata;
                          return meta?.pagerduty_url ? (
                            <a
                              href={meta.pagerduty_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-400"
                            >
                              <ExternalLink className="h-3 w-3" />
                              View in PagerDuty
                            </a>
                          ) : null;
                        })()}
                        {signal.error_code && (
                          <div className="flex items-center gap-2">
                            <Badge variant="destructive">{signal.error_code}</Badge>
                            {signal.error_message && (
                              <span className="text-sm text-red-500">{signal.error_message}</span>
                            )}
                          </div>
                        )}
                        {signal.resource_arn && (
                          <div className="text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded">
                            {signal.resource_arn}
                          </div>
                        )}
                        {signal.user_identity && (
                          <div className="text-xs text-muted-foreground">
                            <span className="font-medium">Actor:</span> {signal.user_identity.arn || signal.user_identity.user_name || 'Unknown'}
                          </div>
                        )}
                        {/* Application error logs dropdown for ApplicationLogErrors signals */}
                        {signal.event_name === 'ApplicationLogErrors' && (() => {
                          const meta = typeof signal.metadata === 'string'
                            ? (() => { try { return JSON.parse(signal.metadata); } catch { return null; } })()
                            : signal.metadata;
                          const errorContext = meta?.error_context;
                          const logGroup = meta?.log_group;
                          if (!errorContext) return null;
                          return (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-sm font-medium text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1">
                                <FileText className="h-3.5 w-3.5" />
                                View Application Error Logs {logGroup && <span className="text-muted-foreground font-normal">({logGroup})</span>}
                              </summary>
                              <div className="mt-2 max-h-64 overflow-y-auto rounded-md bg-muted p-3 text-xs font-mono text-muted-foreground whitespace-pre-wrap border">
                                {errorContext}
                              </div>
                            </details>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                  {incident.signals.length > SIGNALS_PER_PAGE && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <span className="text-xs text-muted-foreground">
                        Showing {(signalsPage - 1) * SIGNALS_PER_PAGE + 1}-{Math.min(signalsPage * SIGNALS_PER_PAGE, incident.signals.length)} of {incident.signals.length} signals
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSignalsPage(p => Math.max(1, p - 1))}
                          disabled={signalsPage === 1}
                        >
                          Previous
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          Page {signalsPage} of {Math.ceil(incident.signals.length / SIGNALS_PER_PAGE)}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSignalsPage(p => Math.min(Math.ceil(incident.signals.length / SIGNALS_PER_PAGE), p + 1))}
                          disabled={signalsPage >= Math.ceil(incident.signals.length / SIGNALS_PER_PAGE)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground text-center py-8">No signals available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        {hasRCA && (
          <TabsContent value="timeline">
            <Card>
              <CardHeader>
                <CardTitle>Event Timeline</CardTitle>
                <CardDescription>
                  Chronological sequence of events related to this incident
                </CardDescription>
              </CardHeader>
              <CardContent>
                {timeline.length > 0 ? (
                  <div className="relative space-y-0">
                    {timeline.map((event, index) => (
                      <div key={index} className="flex gap-4 pb-6 last:pb-0">
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted flex-shrink-0">
                            {getTimelineEventIcon(event.event_type, event.severity)}
                          </div>
                          {index < timeline.length - 1 && (
                            <div className="w-px h-full bg-border mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-muted-foreground">
                              {formatTimeFromISO(event.timestamp)}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {event.source === 'pagerduty_alert' ? 'PagerDuty Alert' : event.source}
                            </Badge>
                            <Badge variant={getSeverityBadgeVariant(event.severity)} className="text-xs">
                              {event.severity}
                            </Badge>
                          </div>
                          <p className="text-sm">{event.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No timeline events available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* CLI Fixes Tab (AWS or OCI) */}
        {hasRCA && (
          <TabsContent value="fixes">
            <Card>
              <CardHeader>
                <CardTitle>Recommended {isOCIIncident ? 'OCI' : 'AWS'} Fixes</CardTitle>
                <CardDescription>
                  {isOCIIncident ? 'OCI' : 'AWS'} CLI commands to remediate this incident. Review carefully before execution.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {totalCliFixes > 0 ? (
                  <div className="space-y-4">
                    {currentFixes.map((fix, index) => (
                      <Collapsible
                        key={index}
                        open={expandedFix === index}
                        onOpenChange={() => setExpandedFix(expandedFix === index ? null : index)}
                      >
                        <div className="border rounded-lg">
                          <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 rounded-t-lg">
                            <div className="flex items-center gap-3">
                              <Terminal className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{fix.service}:{fix.operation}</span>
                              <Badge variant={getRiskLevelBadgeVariant(fix.risk_level)}>
                                {fix.risk_level} risk
                              </Badge>
                              {getFixStatusBadge(fix)}
                            </div>
                            {expandedFix === index ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="p-4 pt-0 space-y-4">
                              <Separator />
                              
                              {/* Fix Status Details - Only show if NOT showing current operation result */}
                              {fix.status === 'applied' && fix.applied_at && !(fixResult && fixResult.index === index && fixResult.result.success) && (
                                <Alert className="border-border bg-muted/30">
                                  <CheckCircle className="h-4 w-4 text-foreground" />
                                  <AlertTitle>Applied Successfully</AlertTitle>
                                  <AlertDescription>
                                    Applied by {fix.applied_by || 'Unknown'} on {new Date(fix.applied_at).toLocaleString()}
                                    {fix.apply_result && (
                                      <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-32">
                                        {/* For async operations, only show the initial CLI output (before SSM command output) */}
                                        {fix.is_async && fix.apply_result.includes('\n\n===')
                                          ? fix.apply_result.substring(0, fix.apply_result.indexOf('\n\n==='))
                                          : fix.apply_result}
                                      </pre>
                                    )}
                                  </AlertDescription>
                                </Alert>
                              )}

                              {fix.status === 'rolled_back' && fix.rolled_back_at && !(fixResult && fixResult.index === index && fixResult.result.success) && (
                                <Alert className="border-blue-500/50 bg-blue-500/10">
                                  <Undo2 className="h-4 w-4 text-blue-500" />
                                  <AlertTitle>Rolled Back</AlertTitle>
                                  <AlertDescription>
                                    Rolled back on {new Date(fix.rolled_back_at).toLocaleString()}
                                    {fix.rollback_result && (
                                      <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-32">
                                        {fix.rollback_result}
                                      </pre>
                                    )}
                                  </AlertDescription>
                                </Alert>
                              )}

                              {fix.status === 'failed' && !(fixResult && fixResult.index === index) && (
                                <Alert variant="destructive">
                                  <XCircle className="h-4 w-4" />
                                  <AlertTitle>Execution Failed</AlertTitle>
                                  <AlertDescription>
                                    {/* Show error in code block - apply_result and apply_error often contain the same content */}
                                    {fix.apply_result ? (
                                      <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-32">
                                        {fix.apply_result}
                                      </pre>
                                    ) : fix.apply_error ? (
                                      <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-32">
                                        {fix.apply_error}
                                      </pre>
                                    ) : (
                                      <span>Unknown error occurred</span>
                                    )}
                                  </AlertDescription>
                                </Alert>
                              )}

                              {/* Async operation status */}
                              {fix.async_status === 'polling' && (
                                <Alert className="border-blue-500/50 bg-blue-500/10">
                                  <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                                  <AlertTitle className="flex items-center gap-2">
                                    Async Operation In Progress
                                    <Badge variant="outline" className="ml-2">Polling</Badge>
                                  </AlertTitle>
                                  <AlertDescription>
                                    <div className="space-y-2">
                                      <p>The AWS operation is being executed asynchronously. SSM commands will run automatically when complete.</p>
                                      {fix.async_operation_arn && (
                                        <p className="text-xs font-mono text-muted-foreground">
                                          Operation ARN: {fix.async_operation_arn}
                                        </p>
                                      )}
                                      {fix.async_last_polled_at && (
                                        <p className="text-xs text-muted-foreground">
                                          Last polled: {new Date(fix.async_last_polled_at).toLocaleString()}
                                        </p>
                                      )}
                                      {fix.async_last_poll_result && (
                                        <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-24">
                                          {fix.async_last_poll_result}
                                        </pre>
                                      )}
                                    </div>
                                  </AlertDescription>
                                </Alert>
                              )}

                              {/* Async Operation Completed status is now shown only in the Post-Completion Commands section */}

                              {fix.async_status === 'timeout' && (
                                <Alert variant="destructive">
                                  <Clock className="h-4 w-4" />
                                  <AlertTitle>Async Operation Timed Out</AlertTitle>
                                  <AlertDescription>
                                    <p>The async operation did not complete within the expected time.</p>
                                    {fix.async_last_poll_result && (
                                      <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-24">
                                        {fix.async_last_poll_result}
                                      </pre>
                                    )}
                                  </AlertDescription>
                                </Alert>
                              )}

                              {fix.async_status === 'failed' && (
                                <Alert variant="destructive">
                                  <XCircle className="h-4 w-4" />
                                  <AlertTitle>Async Operation Failed</AlertTitle>
                                  <AlertDescription>
                                    {fix.apply_error || 'The async operation failed.'}
                                    {fix.async_last_poll_result && (
                                      <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-24">
                                        {fix.async_last_poll_result}
                                      </pre>
                                    )}
                                  </AlertDescription>
                                </Alert>
                              )}

                              {/* Show current operation result (takes precedence over persisted status) */}
                              {fixResult && fixResult.index === index && (
                                <Alert variant={fixResult.result.success ? 'default' : 'destructive'} 
                                       className={fixResult.result.success ? 'border-border bg-muted/30' : 
                                                  ('fix_attempted' in fixResult.result && fixResult.result.fix_attempted && fixResult.result.fixed_command) ? 'border-border bg-muted/30' : ''}>
                                  {fixResult.result.success ? (
                                    <CheckCircle className="h-4 w-4 text-foreground" />
                                  ) : ('fix_attempted' in fixResult.result && fixResult.result.fix_attempted && fixResult.result.fixed_command) ? (
                                    <Wand2 className="h-4 w-4 text-amber-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4" />
                                  )}
                                  <AlertTitle>
                                    {fixResult.result.success 
                                      ? `${fixResult.type === 'apply' ? 'Apply' : 'Rollback'} Successful`
                                      : ('fix_attempted' in fixResult.result && fixResult.result.fix_attempted && fixResult.result.fixed_command)
                                        ? 'Command Failed - AI Suggested Fix Available'
                                        : `${fixResult.type === 'apply' ? 'Apply' : 'Rollback'} Failed`}
                                  </AlertTitle>
                                  <AlertDescription>
                                    {fixResult.result.message}
                                    {/* Show output in code block - error details are included here */}
                                    {fixResult.result.output && (
                                      <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-32">
                                        {fixResult.result.output}
                                      </pre>
                                    )}
                                    {/* Only show error if there's no output (fallback) */}
                                    {!fixResult.result.output && fixResult.result.error && (
                                      <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-32">
                                        {fixResult.result.error}
                                      </pre>
                                    )}
                                    
                                    {/* Show AI-generated fixed command */}
                                    {'fix_attempted' in fixResult.result && fixResult.result.fix_attempted && fixResult.result.fixed_command && (
                                      <div className="mt-4 space-y-3 border-t border-amber-500/30 pt-3">
                                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-medium">
                                          <Wand2 className="h-4 w-4" />
                                          AI-Suggested Fixed Command
                                        </div>
                                        {'fix_explanation' in fixResult.result && fixResult.result.fix_explanation && (
                                          <p className="text-sm text-muted-foreground">
                                            {fixResult.result.fix_explanation}
                                          </p>
                                        )}
                                        {'fix_changes_made' in fixResult.result && fixResult.result.fix_changes_made && fixResult.result.fix_changes_made.length > 0 && (
                                          <div className="text-sm">
                                            <span className="font-medium">Changes made:</span>
                                            <ul className="list-disc list-inside ml-2 text-muted-foreground">
                                              {fixResult.result.fix_changes_made.map((change, i) => (
                                                <li key={i}>{change}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        <pre className="bg-muted p-3 rounded text-xs overflow-x-auto font-mono">
                                          {fixResult.result.fixed_command}
                                        </pre>
                                        <p className="text-xs text-muted-foreground">
                                          Review the suggested command above. You can copy and run it manually, or contact support if you need assistance.
                                        </p>
                                      </div>
                                    )}
                                    
                                    {/* Show cannot auto-fix message */}
                                    {'cannot_auto_fix' in fixResult.result && fixResult.result.cannot_auto_fix && 'fix_explanation' in fixResult.result && (
                                      <div className="mt-3 p-3 bg-muted/50 rounded border border-red-500/20">
                                        <p className="text-sm font-medium text-red-600 dark:text-red-400">Cannot automatically fix this issue</p>
                                        <p className="text-sm text-muted-foreground mt-1">{fixResult.result.fix_explanation}</p>
                                      </div>
                                    )}
                                  </AlertDescription>
                                </Alert>
                              )}

                              {/* Show validation result */}
                              {validationResult && validationResult.index === index && (
                                <Alert 
                                  variant={validationResult.result.validation_status === 'fixed' ? 'default' : 'destructive'}
                                  className={validationResult.result.validation_status === 'fixed' ? 'border-border bg-muted/30' : 
                                             validationResult.result.validation_status === 'unknown' ? 'border-yellow-500/50 bg-yellow-500/10' : ''}
                                >
                                  {validationResult.result.validation_status === 'fixed' ? (
                                    <CheckCircle className="h-4 w-4 text-foreground" />
                                  ) : validationResult.result.validation_status === 'unknown' ? (
                                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4" />
                                  )}
                                  <AlertTitle>
                                    Validation: {validationResult.result.validation_status === 'fixed' ? 'Fix Verified ✓' :
                                                 validationResult.result.validation_status === 'not_fixed' ? 'Not Fixed' :
                                                 validationResult.result.validation_status === 'recurring' ? 'Issue Recurring' :
                                                 'Unable to Verify'}
                                  </AlertTitle>
                                  <AlertDescription>
                                    <p>{validationResult.result.validation_summary}</p>
                                    {validationResult.result.validation_details && validationResult.result.validation_details.length > 0 && (
                                      <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                                        {validationResult.result.validation_details.map((detail, i) => (
                                          <li key={i}>{detail}</li>
                                        ))}
                                      </ul>
                                    )}
                                    {validationResult.result.aws_commands_executed > 0 && (
                                      <p className="mt-2 text-xs text-muted-foreground">
                                        {validationResult.result.aws_commands_executed} AWS commands executed
                                      </p>
                                    )}
                                    {validationResult.result.error && (
                                      <p className="mt-1 text-red-500">{validationResult.result.error}</p>
                                    )}
                                  </AlertDescription>
                                </Alert>
                              )}

                              <div>
                                <h4 className="text-sm font-medium mb-2">Explanation</h4>
                                <p className="text-sm text-muted-foreground">{fix.explanation}</p>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium mb-2">Resource</h4>
                                <code className="text-xs bg-muted px-2 py-1 rounded block">
                                  {getFixResourceIdentifier(fix) || (fix.ssm_instance_id ? `arn:aws:ec2:${fix.ssm_region || 'us-west-2'}:*:instance/${fix.ssm_instance_id}` : 'N/A')}
                                </code>
                              </div>
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-medium flex items-center gap-2">
                                    {isOCIIncident ? 'OCI' : 'AWS'} CLI Command
                                  </h4>
                                  <div className="flex items-center gap-1">
                                    {hasPlaceholders(fix.cli_command) && fix.status !== 'applied' && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleEditPlaceholders(index, fix)}
                                            >
                                              <Pencil className="h-4 w-4 text-muted-foreground" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            Edit placeholders
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => copyToClipboard(fix.cli_command, index)}
                                          >
                                            {copiedIndex === index ? (
                                              <CheckCircle className="h-4 w-4 text-foreground" />
                                            ) : (
                                              <Copy className="h-4 w-4" />
                                            )}
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {copiedIndex === index ? 'Copied!' : 'Copy command'}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                </div>
                                <pre className="bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 p-4 rounded-lg text-xs overflow-x-auto border border-zinc-200 dark:border-zinc-700 whitespace-pre-wrap">
                                  {renderCommandWithPlaceholders(fix.cli_command, hasPlaceholders(fix.cli_command) ? () => handleEditPlaceholders(index, fix) : undefined)}
                                </pre>
                              </div>

                              {/* Rollback Command */}
                              {fix.rollback_command && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium flex items-center gap-2">
                                      Rollback Command
                                    </h4>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => copyToClipboard(fix.rollback_command!, index + 1000)}
                                          >
                                            {copiedIndex === index + 1000 ? (
                                              <CheckCircle className="h-4 w-4 text-foreground" />
                                            ) : (
                                              <Copy className="h-4 w-4" />
                                            )}
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {copiedIndex === index + 1000 ? 'Copied!' : 'Copy rollback command'}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                  <pre className="bg-muted/50 text-foreground p-4 rounded-lg text-xs overflow-x-auto border border-border whitespace-pre-wrap">
                                    {renderCommandWithPlaceholders(fix.rollback_command, hasPlaceholders(fix.rollback_command) ? () => handleEditPlaceholders(index, fix) : undefined)}
                                  </pre>
                                </div>
                              )}

                              {fix.parameters && Object.keys(fix.parameters).length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium mb-2">API Parameters</h4>
                                  <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto">
                                    {stripPlaceholderBraces(JSON.stringify(fix.parameters, null, 2))}
                                  </pre>
                                </div>
                              )}

                              {/* SSM Commands (Shell commands to run on EC2) */}
                              {fix.ssm_commands && fix.ssm_commands.length > 0 && (
                                <div className={fix.is_async ? 'border rounded-lg p-4 border-dashed border-muted-foreground/30' : ''}>
                                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Terminal className="h-4 w-4" />
                                    {fix.is_async ? 'Post-Completion Commands' : 'SSM Commands'} (EC2: {fix.ssm_instance_id})
                                  </h4>
                                  <p className="text-xs text-muted-foreground mb-3">
                                    {fix.is_async 
                                      ? 'These commands will run automatically after the async operation completes. The server polls for status and executes them when ready.'
                                      : 'These commands will be executed on the EC2 instance via AWS Systems Manager after the AWS CLI fix is applied.'
                                    }
                                  </p>
                                  {fix.is_async && fix.async_status === 'polling' && (
                                    <Alert className="mb-3 border-blue-500/30 bg-blue-500/10">
                                      <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                                      <AlertDescription className="text-xs">
                                        Async operation in progress. SSM commands will execute automatically when complete.
                                      </AlertDescription>
                                    </Alert>
                                  )}
                                  {fix.is_async && fix.async_status === 'completed' && (
                                    <Alert className="mb-3 border-border bg-muted/30">
                                      <CheckCircle className="h-4 w-4 text-foreground" />
                                      <AlertDescription className="text-xs">
                                        <p>Async operation completed. SSM commands have been executed.</p>
                                        {/* SSM command output */}
                                        {fix.apply_result && fix.apply_result.includes('===') && (
                                          <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto overflow-y-auto max-h-32 whitespace-pre-wrap">
                                            {fix.apply_result.substring(fix.apply_result.indexOf('\n\n===') + 2).trim()}
                                          </pre>
                                        )}
                                      </AlertDescription>
                                    </Alert>
                                  )}
                                  <div className="space-y-4">
                                    {/* Files to Create - Prerequisite */}
                                    {fix.ssm_commands.some(cmd => cmd.files && cmd.files.length > 0) && (
                                      <div className="border rounded-lg p-3 bg-muted/50">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-600">Prerequisite</Badge>
                                          <span className="text-sm font-medium">Files to Create</span>
                                        </div>
                                        <div className="space-y-3">
                                          {fix.ssm_commands.map((cmd, cmdIdx) => 
                                            cmd.files?.map((file, fileIdx) => (
                                              <div key={`${cmdIdx}-${fileIdx}`} className="border rounded p-2 bg-background/50">
                                                <div className="flex items-center justify-between mb-1">
                                                  <div>
                                                    <code className="text-xs font-mono text-muted-foreground">{file.path}</code>
                                                    {file.description && (
                                                      <p className="text-xs text-muted-foreground mt-0.5">{file.description}</p>
                                                    )}
                                                  </div>
                                                  <TooltipProvider>
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          onClick={() => copyToClipboard(file.content, index + 3000 + cmdIdx * 10 + fileIdx)}
                                                        >
                                                          {copiedIndex === index + 3000 + cmdIdx * 10 + fileIdx ? (
                                                            <CheckCircle className="h-4 w-4 text-foreground" />
                                                          ) : (
                                                            <Copy className="h-4 w-4" />
                                                          )}
                                                        </Button>
                                                      </TooltipTrigger>
                                                      <TooltipContent>
                                                        {copiedIndex === index + 3000 + cmdIdx * 10 + fileIdx ? 'Copied!' : 'Copy file content'}
                                                      </TooltipContent>
                                                    </Tooltip>
                                                  </TooltipProvider>
                                                </div>
                                                <pre className="bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 p-2 rounded text-xs overflow-x-auto border border-zinc-200 dark:border-zinc-700">
                                                  {file.content}
                                                </pre>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {/* SSM Command Steps */}
                                    {fix.ssm_commands.map((cmd, cmdIdx) => (
                                      <div key={cmdIdx} className="border rounded-lg p-3 bg-muted/50">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="text-xs">Step {cmdIdx + 1}</Badge>
                                            <span className="text-sm font-medium">{cmd.description}</span>
                                          </div>
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => copyToClipboard(cmd.command, index + 4000 + cmdIdx)}
                                                >
                                                  {copiedIndex === index + 4000 + cmdIdx ? (
                                                    <CheckCircle className="h-4 w-4 text-foreground" />
                                                  ) : (
                                                    <Copy className="h-4 w-4" />
                                                  )}
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                {copiedIndex === index + 4000 + cmdIdx ? 'Copied!' : 'Copy command'}
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        </div>
                                        <pre className="bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap border border-zinc-200 dark:border-zinc-700">
                                          {cmd.command}
                                        </pre>
                                        {cmd.timeout && cmd.timeout !== 300 && (
                                          <p className="text-xs text-muted-foreground mt-2">Timeout: {cmd.timeout}s</p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Action Buttons */}
                              <Separator />
                              <div className="flex items-center gap-3">
                                {/* Apply Button */}
                                {(!fix.status || fix.status === 'pending' || fix.status === 'failed' || fix.status === 'rolled_back') && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => setShowApplyDialog(index)}
                                    disabled={applyingFix === index || rollingBackFix !== null}
                                    className="bg-foreground text-background hover:bg-foreground/90"
                                  >
                                    {applyingFix === index ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Applying...
                                      </>
                                    ) : (
                                      <>
                                        <Zap className="mr-2 h-4 w-4" />
                                        Apply Fix
                                      </>
                                    )}
                                  </Button>
                                )}

                                {/* Rollback Button */}
                                {fix.status === 'applied' && fix.rollback_command && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowRollbackDialog(index)}
                                    disabled={rollingBackFix === index || applyingFix !== null || validatingFix !== null}
                                    className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
                                  >
                                    {rollingBackFix === index ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Rolling back...
                                      </>
                                    ) : (
                                      <>
                                        <Undo2 className="mr-2 h-4 w-4" />
                                        Rollback
                                      </>
                                    )}
                                  </Button>
                                )}

                                {/* Validate Fix Button */}
                                {fix.status === 'applied' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowValidateDialog(index)}
                                    disabled={validatingFix === index || applyingFix !== null || rollingBackFix !== null}
                                    className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                                  >
                                    {validatingFix === index ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Validating...
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle className="mr-2 h-4 w-4" />
                                        Validate Fix
                                      </>
                                    )}
                                  </Button>
                                )}

                                {fix.status === 'applied' && !fix.rollback_command && (
                                  <span className="text-xs text-muted-foreground">
                                    No rollback command available for this fix
                                  </span>
                                )}
                              </div>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    {incident.cloud_provider === 'oci' ? 'No OCI fixes recommended' : 'No AWS fixes recommended'}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* IaC Fixes Tab */}
        {iacRepos.length > 0 && (
          <TabsContent value="iac">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  IaC Fixes
                  {iacRepos.length > 0 && (
                    <Badge variant="outline" className="ml-2 capitalize">
                      {iacRepos[0].iac_type}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Infrastructure as Code changes to remediate this incident via PR
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTerraformFixes ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">Loading IaC fixes...</span>
                  </div>
                ) : terraformFixes.length > 0 ? (
                  <div className="space-y-4">
                    {terraformFixes.map((fix) => (
                      <Collapsible
                        key={fix.id}
                        open={expandedTerraformFix === fix.id}
                        onOpenChange={() => setExpandedTerraformFix(expandedTerraformFix === fix.id ? null : fix.id)}
                      >
                        <div className="border rounded-lg">
                          <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 rounded-t-lg">
                            <div className="flex items-center gap-3">
                                                            <span className="font-medium font-mono text-sm">{fix.file_path}</span>
                              {fix.pr_status === 'pr_created' ? (
                                <Badge variant="default" className="bg-foreground text-background">PR Created</Badge>
                              ) : fix.pr_status === 'merged' ? (
                                <Badge variant="default" className="bg-purple-600">Merged</Badge>
                              ) : (
                                <Badge variant="outline">Ready for PR</Badge>
                              )}
                            </div>
                            {expandedTerraformFix === fix.id ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="p-4 pt-0 space-y-4">
                              <Separator />

                              {/* Explanation */}
                              {fix.explanation && (
                                <div>
                                  <h4 className="text-sm font-medium mb-2">Explanation</h4>
                                  <p className="text-sm text-muted-foreground">{fix.explanation}</p>
                                </div>
                              )}

                              {/* Resource Info */}
                              {fix.resource_type && (
                                <div>
                                  <h4 className="text-sm font-medium mb-2">Resource Type</h4>
                                  <code className="text-xs bg-muted px-2 py-1 rounded">
                                    {fix.resource_type}
                                  </code>
                                </div>
                              )}

                              {/* Diff View */}
                              {fix.diff_content && (
                                <div>
                                  <h4 className="text-sm font-medium mb-2">Changes</h4>
                                  <pre className="bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 p-4 rounded-lg text-xs overflow-x-auto max-h-96 border border-zinc-200 dark:border-zinc-700">
                                    {fix.diff_content}
                                  </pre>
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className="flex items-center gap-2 pt-2">
                                {fix.pr_url ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(fix.pr_url, '_blank')}
                                  >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    View Pull Request
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={() => handleCreateTerraformPR(fix.id)}
                                    disabled={creatingPR === fix.id}
                                    className="bg-foreground text-background hover:bg-foreground/90"
                                  >
                                    {creatingPR === fix.id ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Creating PR...
                                      </>
                                    ) : (
                                      <>
                                        <ExternalLink className="mr-2 h-4 w-4" />
                                        Create Pull Request
                                      </>
                                    )}
                                  </Button>
                                )}

                                {/* Copy proposed content */}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          navigator.clipboard.writeText(fix.proposed_content);
                                        }}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Copy proposed content</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                                        <p>No IaC fixes available for this incident.</p>
                    <p className="text-sm mt-1">
                      IaC fixes are automatically generated during incident analysis when applicable resources are found in your {iacRepos.length > 0 ? iacRepos[0].iac_type.charAt(0).toUpperCase() + iacRepos[0].iac_type.slice(1) : 'IaC'} configuration.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Code Fixes Tab (for application-level incidents) */}
        {hasRCA && incident.rca_result?.is_application_level_failure && (() => {
          const codeFixes = incident.rca_result!.code_fixes || [];
          const codeProvider = incident.rca_result!.code_provider || 'github';
          const codeFixStatus = incident.rca_result!.code_fix_status || '';
          return (
          <TabsContent value="code-fixes">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>
                      Code Fixes ({codeFixes.length})
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Application code changes to fix the underlying bug
                    </CardDescription>
                  </div>
                  {codeFixes.length > 0 && (codeFixPrUrl ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(codeFixPrUrl, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Pull Request
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={creatingCodeFixPR}
                      onClick={async () => {
                        setCreatingCodeFixPR(true);
                        try {
                          const isGitLab = codeProvider === 'gitlab';
                          const fixesByRepo = new Map<string, typeof codeFixes>();
                          for (const fix of codeFixes) {
                            const repoId = fix.repo_id || 'default';
                            if (!fixesByRepo.has(repoId)) fixesByRepo.set(repoId, []);
                            fixesByRepo.get(repoId)!.push(fix);
                          }
                          let lastPrUrl: string | null = null;
                          const entries = Array.from(fixesByRepo.entries());
                          for (const [repoId, repoFixes] of entries) {
                            const requestData = {
                              incident_id: incident.id,
                              repository_id: repoId,
                              fixes: repoFixes.map((f: typeof codeFixes[0]) => ({
                                file_path: f.file_path,
                                fixed_content: f.fixed_code,
                                original_content: f.original_code || '',
                                line_start: f.line_start || 0,
                                line_end: f.line_end || 0,
                              })),
                              pr_title: `[KestrelAI] Code Fix: ${repoFixes.length} file(s) for ${incident.rca_result?.incident_type || 'incident'}`,
                              pr_description: `Automated code fix for [cloud incident](${window.location.href}).\n\nFiles changed:\n${repoFixes.map((f: typeof codeFixes[0]) => `- ${f.file_path}: ${f.explanation}`).join('\n')}`,
                              source_type: 'cloud_incident',
                              source_id: incident.id,
                            };
                            if (isGitLab) {
                              const result = await apiClient.createCodeFixMergeRequest(requestData);
                              if (result.success && result.mr_url) lastPrUrl = result.mr_url;
                            } else {
                              const result = await apiClient.createCodeFixPullRequest(requestData);
                              if (result.success && result.pr_url) lastPrUrl = result.pr_url;
                            }
                          }
                          if (lastPrUrl) {
                            setCodeFixPrUrl(lastPrUrl);
                            window.open(lastPrUrl, '_blank');
                          }
                        } catch (err: any) {
                          console.error('Failed to create PR:', err);
                          alert(err.response?.data?.message || err.message || 'Failed to create PR');
                        } finally {
                          setCreatingCodeFixPR(false);
                        }
                      }}
                      className="flex items-center gap-2"
                    >
                      {creatingCodeFixPR ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <GitPullRequest className="h-4 w-4" />
                      )}
                      {creatingCodeFixPR ? 'Creating...' : `Create PR for All ${codeFixes.length} Fixes`}
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {codeFixes.length === 0 && codeFixStatus === 'analyzing' && (
                  <div className="flex items-center gap-3 p-4 rounded-md border bg-muted/50">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Analyzing application code to generate fixes...
                    </p>
                  </div>
                )}
                {codeFixes.length === 0 && codeFixStatus === 'attempted' && (
                  <div className="p-4 rounded-md border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/30">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Kestrel code analysis was attempted but couldn't automatically generate a fix.
                        </p>
                        {incident.rca_result?.suspected_source_files && incident.rca_result.suspected_source_files.length > 0 && (
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                            Review the suspected source files to manually fix the issue, or retry the analysis.
                          </p>
                        )}
                        {incident.rca_result?.suspected_source_files && incident.rca_result.suspected_source_files.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {incident.rca_result.suspected_source_files.map((sf: any, i: number) => (
                              <Badge key={i} variant="secondary" className="font-mono text-xs">
                                {typeof sf === 'string' ? sf : sf.file_path}{typeof sf !== 'string' && sf.line_number ? `:${sf.line_number}` : ''}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={retryingCodeAnalysis}
                            onClick={async () => {
                              setRetryingCodeAnalysis(true);
                              try {
                                await apiClient.retryCloudCodeAnalysis(incident.id);
                              } catch (err: any) {
                                console.error('Failed to retry code analysis:', err);
                              } finally {
                                setRetryingCodeAnalysis(false);
                              }
                            }}
                          >
                            {retryingCodeAnalysis ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            {retryingCodeAnalysis ? 'Retrying...' : 'Retry Analysis'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  {codeFixes.map((fix, index) => {
                    return (
                    <Collapsible key={index} defaultOpen={index === 0}>
                      <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden">
                        <CollapsibleTrigger className="w-full hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                          <div className="flex items-center justify-between p-4 gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <Badge variant="outline" className="font-mono text-xs flex-shrink-0">
                                {fix.language || 'code'}
                              </Badge>
                              <span className="font-mono text-sm truncate">{fix.file_path}</span>
                            </div>
                            <Badge variant="outline" className="text-xs whitespace-nowrap flex-shrink-0 font-mono">
                              L{fix.line_start}-{fix.line_end}
                            </Badge>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="p-4 pt-0 space-y-4 overflow-hidden">
                            {fix.explanation && (
                              <div className="text-sm text-muted-foreground bg-zinc-50 dark:bg-zinc-900/50 p-3 rounded-md border border-zinc-200 dark:border-zinc-700">
                                <div className="mt-0">{parseFormattedText(fix.explanation)}</div>
                              </div>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(fix.fixed_code); }}>
                                <Copy className="h-3 w-3 mr-1" /> Copy
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => {
                                const blob = new Blob([fix.fixed_code], { type: 'text/plain' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = fix.file_path.split('/').pop() || 'code.txt';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              }}>
                                <Download className="h-3 w-3 mr-1" /> Download
                              </Button>
                            </div>
                            {fix.original_code ? (
                              <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden max-w-full">
                                <div className="bg-zinc-100 dark:bg-zinc-800 px-4 py-2 text-xs font-mono flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700">
                                  <span className="text-zinc-600 dark:text-zinc-400">diff</span>
                                  <span className="text-zinc-500 dark:text-zinc-500">Lines {fix.line_start}-{fix.line_end}</span>
                                </div>
                                <div className="font-mono text-xs overflow-x-auto max-h-96 bg-zinc-50 dark:bg-zinc-900">
                                  {createDiff(fix.original_code, fix.fixed_code).map((line, idx) => (
                                    <div
                                      key={idx}
                                      className={cn(
                                        "px-4 py-0.5 whitespace-pre",
                                        line.type === 'removed' && "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300",
                                        line.type === 'added' && "bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300",
                                        line.type === 'unchanged' && "text-zinc-600 dark:text-zinc-400"
                                      )}
                                    >
                                      <span className="select-none mr-4 text-zinc-400 dark:text-zinc-600 w-8 inline-block text-right">
                                        {line.lineNumber}
                                      </span>
                                      <span className="select-none mr-2">
                                        {line.type === 'removed' ? '-' : line.type === 'added' ? '+' : ' '}
                                      </span>
                                      {line.line}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <pre className="p-4 rounded-lg border border-zinc-200 dark:border-zinc-700 font-mono text-xs overflow-x-auto max-h-96 bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100">
                                <code>{fix.fixed_code}</code>
                              </pre>
                            )}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          );
        })()}

        {/* Tribal Knowledge Tab */}
        {hasRCA && incident.rca_result?.tribal_knowledge_used && incident.rca_result.tribal_knowledge_used.length > 0 && (
          <TabsContent value="tribal-knowledge">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Tribal Knowledge Used
                </CardTitle>
                <CardDescription>
                  Relevant past incidents, documentation, and discussions that informed this analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const totalSnippets = incident.rca_result!.tribal_knowledge_used!.length;
                  const totalPages = Math.ceil(totalSnippets / TRIBAL_KNOWLEDGE_PER_PAGE);
                  const startIndex = (tribalKnowledgePage - 1) * TRIBAL_KNOWLEDGE_PER_PAGE;
                  const endIndex = startIndex + TRIBAL_KNOWLEDGE_PER_PAGE;
                  const currentSnippets = incident.rca_result!.tribal_knowledge_used!.slice(startIndex, endIndex);
                  
                  return (
                    <>
                      <div className="space-y-4">
                        {currentSnippets.map((snippet, index) => (
                          <div key={startIndex + index} className="border rounded-lg p-4 space-y-3 bg-gradient-to-r from-amber-500/5 to-transparent">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 p-1.5 rounded-lg bg-muted">
                                  <TribalKnowledgeSourceIcon sourceType={snippet.source_type} className="h-5 w-5" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-sm">{snippet.title}</h4>
                                  <span className="text-xs text-muted-foreground">{snippet.source_name}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs capitalize flex items-center gap-1.5">
                                  <TribalKnowledgeSourceIcon sourceType={snippet.source_type} className="h-3 w-3" />
                                  {snippet.source_type}
                                </Badge>
                                <div className="flex items-center gap-1">
                                  <Star className="h-3 w-3 text-amber-500" />
                                  <span className="text-xs text-muted-foreground">
                                    {(snippet.relevance_score * 100).toFixed(0)}% relevant
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <p className="text-sm text-muted-foreground line-clamp-3">
                              {snippet.content}
                            </p>
                            
                            {snippet.relevance_reason && (
                              <div className="bg-muted/50 rounded p-3 border border-amber-500/20">
                                <div className="flex items-center gap-2 mb-1">
                                  <Lightbulb className="h-4 w-4 text-amber-500" />
                                  <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Why this is relevant</span>
                                </div>
                                <p className="text-sm text-muted-foreground">{snippet.relevance_reason}</p>
                              </div>
                            )}
                            
                            {snippet.key_insights && snippet.key_insights.length > 0 && (
                              <div className="space-y-1">
                                <span className="text-xs font-medium text-muted-foreground">Key Insights:</span>
                                <ul className="space-y-1">
                                  {snippet.key_insights.map((insight, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                      <span className="text-amber-500 mt-1">•</span>
                                      <span>{insight}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between pt-2 border-t">
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                {snippet.author && <span>By: {snippet.author}</span>}
                                {snippet.timestamp && (
                                  <span>{new Date(snippet.timestamp).toLocaleDateString()}</span>
                                )}
                              </div>
                              {snippet.url && (
                                <Button variant="outline" size="sm" asChild>
                                  <a href={snippet.url} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="mr-2 h-3 w-3" />
                                    View Source
                                  </a>
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t">
                          <span className="text-sm text-muted-foreground">
                            Showing {startIndex + 1}-{Math.min(endIndex, totalSnippets)} of {totalSnippets} sources
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setTribalKnowledgePage(p => Math.max(1, p - 1))}
                              disabled={tribalKnowledgePage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-muted-foreground px-2">
                              Page {tribalKnowledgePage} of {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setTribalKnowledgePage(p => Math.min(totalPages, p + 1))}
                              disabled={tribalKnowledgePage === totalPages}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Affected Resources Tab */}
        <TabsContent value="resources">
          <Card>
            <CardHeader>
              <CardTitle>Affected Resources</CardTitle>
              <CardDescription>
                {incident.cloud_provider === 'oci' 
                  ? 'OCI resources impacted by this incident'
                  : 'AWS resources impacted by this incident'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {allAffectedResources.length > 0 ? (
                <div className="space-y-3">
                  {allAffectedResources.map((resource, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{resource.resource_name || resource.resource_id || (resource.arn ? resource.arn.split('/').pop() || resource.arn.split(':').pop() : 'Unknown')}</span>
                            </div>
                            {resource.region && (
                              <Badge variant="outline">{resource.region}</Badge>
                            )}
                          </div>
                          {resource.arn && (
                            <div className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">
                              {resource.arn}
                            </div>
                          )}
                          {resource.resource_type && (
                            <div className="text-xs text-muted-foreground mt-2">
                              Type: {resource.resource_type}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No affected resources identified</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incident History Tab */}
        {incident.previous_incidents && incident.previous_incidents.length > 0 && (
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>
                  Incident History
                </CardTitle>
                <CardDescription>
                  {incident.previous_incidents.length} previous occurrence{incident.previous_incidents.length !== 1 ? 's' : ''} of {incident.type} for {incident.affected_resources?.[0]?.resource_name || incident.affected_resources?.[0]?.arn || 'this resource'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-border rounded-lg border overflow-hidden">
                  {incident.previous_incidents
                    .slice(historyPage * HISTORY_PER_PAGE, (historyPage + 1) * HISTORY_PER_PAGE)
                    .map((prev) => {
                      const displayType = getCloudIncidentTypeLabel(prev.incident_type || prev.type);
                      const codeFixCount = prev.code_fix_count || 0;
                      const cliFixCount = (prev.api_fix_count || 0) + (prev.oci_cli_fix_count || 0);
                      const iacFixCount = prev.terraform_fix_count || 0;
                      // For ApplicationLogErrors, prioritize code fixes; otherwise CLI > IaC > code
                      const incType = prev.incident_type || prev.type || '';
                      const fixCount = incType === 'ApplicationLogErrors'
                        ? (codeFixCount > 0 ? codeFixCount : (cliFixCount > 0 ? cliFixCount : iacFixCount))
                        : (cliFixCount > 0 ? cliFixCount : (iacFixCount > 0 ? iacFixCount : codeFixCount));
                      return (
                        <Link
                          key={prev.id}
                          to={`/cloud-incidents/${prev.id}`}
                          className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={cn(
                              "w-2 h-2 rounded-full flex-shrink-0",
                              prev.status === 'remediation_applied' || prev.status === 'resolved_confirmed'
                                ? "bg-green-500" : "bg-muted-foreground"
                            )} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{displayType}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatDateTime(prev.detected_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-muted-foreground capitalize">
                              {prev.status === 'remediation_applied' ? 'Remediated' :
                               prev.status === 'resolved_confirmed' ? 'Resolved' :
                               prev.status === 'resolved' ? 'Analyzed' :
                               prev.status}
                            </span>
                            {fixCount > 0 && (
                              <Badge variant="secondary" className="text-xs font-normal">
                                {fixCount} fix{fixCount !== 1 ? 'es' : ''}
                              </Badge>
                            )}
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </Link>
                      );
                    })}
                </div>

                {/* Pagination */}
                {incident.previous_incidents.length > HISTORY_PER_PAGE && (
                  <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                    <span>
                      Showing {historyPage * HISTORY_PER_PAGE + 1}–{Math.min((historyPage + 1) * HISTORY_PER_PAGE, incident.previous_incidents.length)} of {incident.previous_incidents.length}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage === 0}
                        onClick={() => setHistoryPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={(historyPage + 1) * HISTORY_PER_PAGE >= incident.previous_incidents.length}
                        onClick={() => setHistoryPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Resolve Dialog */}
      <AlertDialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Incident as Resolved?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the cloud incident as resolved. This action can be used when the incident
              has been investigated and any necessary remediation has been applied.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResolveIncident} disabled={resolvingIncident}>
              {resolvingIncident ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resolving...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Mark Resolved
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Apply Fix Dialog */}
      <AlertDialog open={showApplyDialog !== null} onOpenChange={() => setShowApplyDialog(null)}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Apply {isOCIIncident ? 'OCI' : 'AWS'} Fix?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  This will execute the following {isOCIIncident ? 'OCI' : 'AWS'} CLI command in your {isOCIIncident ? 'OCI tenancy' : 'AWS account'}. 
                  Please review it carefully before proceeding.
                </p>
                {showApplyDialog !== null && currentFixes[showApplyDialog] && (
                  <>
                    {hasPlaceholders(currentFixes[showApplyDialog].cli_command) && (
                      <Alert className="border-cyan-300 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-950">
                        <AlertTriangle className="h-4 w-4 text-cyan-600" />
                        <AlertTitle className="text-sm text-cyan-800 dark:text-cyan-200">
                          Placeholders Need Values
                        </AlertTitle>
                        <AlertDescription className="text-xs text-cyan-700 dark:text-cyan-300">
                          The following placeholders need values: [{extractPlaceholders(currentFixes[showApplyDialog].cli_command).join(', ')}].
                          <br />
                          Close this dialog and click the highlighted placeholder in the command to edit its value.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="mt-3">
                      <span className="text-xs font-medium text-muted-foreground">Command:</span>
                      <pre className="bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 p-3 rounded-lg text-xs mt-1 whitespace-pre-wrap break-all max-h-48 overflow-y-auto border border-zinc-200 dark:border-zinc-700">
                        {renderCommandWithPlaceholders(currentFixes[showApplyDialog].cli_command)}
                      </pre>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={getRiskLevelBadgeVariant(currentFixes[showApplyDialog].risk_level)}>
                        {currentFixes[showApplyDialog].risk_level} risk
                      </Badge>
                      {currentFixes[showApplyDialog].rollback_command ? (
                        <span className="text-xs text-green-600"> Rollback available</span>
                      ) : (
                        <span className="text-xs text-amber-600"> No rollback available</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => showApplyDialog !== null && handleApplyFix(showApplyDialog)}
              disabled={showApplyDialog !== null && currentFixes[showApplyDialog] && hasPlaceholders(currentFixes[showApplyDialog].cli_command)}
              className="bg-foreground text-background hover:bg-foreground/90 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Zap className="mr-2 h-4 w-4" />
              Apply Fix
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rollback Fix Dialog */}
      <AlertDialog open={showRollbackDialog !== null} onOpenChange={() => setShowRollbackDialog(null)}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-amber-500" />
              Rollback {isOCIIncident ? 'OCI' : 'AWS'} Fix?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  This will execute the rollback command to undo the previously applied fix.
                  Please review it carefully before proceeding.
                </p>
                {showRollbackDialog !== null && currentFixes[showRollbackDialog] && (
                  <>
                    {currentFixes[showRollbackDialog].rollback_command && hasPlaceholders(currentFixes[showRollbackDialog].rollback_command!) && (
                      <Alert className="border-cyan-300 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-950">
                        <AlertTriangle className="h-4 w-4 text-cyan-600" />
                        <AlertTitle className="text-sm text-cyan-800 dark:text-cyan-200">
                          Placeholders Need Values
                        </AlertTitle>
                        <AlertDescription className="text-xs text-cyan-700 dark:text-cyan-300">
                          The following placeholders need values: [{extractPlaceholders(currentFixes[showRollbackDialog].rollback_command!).join(', ')}].
                          <br />
                          Close this dialog and click the highlighted placeholder in the command to edit its value.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="mt-3">
                      <span className="text-xs font-medium text-muted-foreground">Rollback Command:</span>
                      <pre className="bg-amber-50 dark:bg-amber-950 text-amber-800 dark:text-amber-100 p-3 rounded-lg text-xs mt-1 whitespace-pre-wrap break-all max-h-48 overflow-y-auto border border-amber-200 dark:border-amber-800">
                        {currentFixes[showRollbackDialog].rollback_command && renderCommandWithPlaceholders(currentFixes[showRollbackDialog].rollback_command!)}
                      </pre>
                    </div>
                    <div className="mt-3">
                      <span className="text-xs font-medium text-muted-foreground">Original Fix Applied:</span>
                      <pre className="bg-muted p-3 rounded-lg text-xs mt-1 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                        {currentFixes[showRollbackDialog].cli_command}
                      </pre>
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => showRollbackDialog !== null && handleRollbackFix(showRollbackDialog)}
              disabled={showRollbackDialog !== null && currentFixes[showRollbackDialog] && !!(currentFixes[showRollbackDialog].rollback_command && hasPlaceholders(currentFixes[showRollbackDialog].rollback_command!))}
              className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Undo2 className="mr-2 h-4 w-4" />
              Rollback
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Validate Fix Dialog */}
      <AlertDialog open={showValidateDialog !== null} onOpenChange={() => setShowValidateDialog(null)}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-blue-500" />
              Validate {isOCIIncident ? 'OCI' : 'AWS'} Fix?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  This will run an AI-powered investigation using {isOCIIncident ? 'OCI' : 'AWS'} CLI commands to verify that the applied fix
                  is working correctly and the incident has been resolved.
                </p>
                {showValidateDialog !== null && currentFixes[showValidateDialog] && (
                  <>
                    <div className="mt-3">
                      <span className="text-xs font-medium text-muted-foreground">Applied Fix Command:</span>
                      <pre className="bg-blue-950 text-blue-100 p-3 rounded-lg text-xs mt-1 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                        {currentFixes[showValidateDialog].cli_command}
                      </pre>
                    </div>
                    <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <p className="text-xs text-blue-400">
                        <strong>What happens:</strong> The AI agent will check the current state of the affected {incident.cloud_provider === 'oci' ? 'OCI' : 'AWS'} resources,
                        verify the configuration is correct, and look for any recurring issues or errors since the fix was applied.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => showValidateDialog !== null && handleValidateFix(showValidateDialog)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Validate Fix
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Placeholders Dialog */}
      <AlertDialog open={editingPlaceholders !== null} onOpenChange={() => setEditingPlaceholders(null)}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-cyan-500" />
              Edit CLI Command Placeholders
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  The CLI command contains placeholders that need to be filled in before execution.
                  Enter the appropriate values below.
                </p>
                
                {editingPlaceholders && (
                  <div className="space-y-4 mt-4">
                    {editingPlaceholders.placeholders.map((placeholder) => (
                      <div key={placeholder.name} className="space-y-2">
                        <Label htmlFor={`placeholder-${placeholder.name}`} className="text-sm font-medium">
                          {placeholder.name}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {placeholder.description}
                          {placeholder.example && (
                            <span className="text-cyan-600 dark:text-cyan-400 ml-1">
                              (e.g., {placeholder.example})
                            </span>
                          )}
                        </p>
                        <Input
                          id={`placeholder-${placeholder.name}`}
                          value={placeholder.value || ''}
                          onChange={(e) => handlePlaceholderChange(placeholder.name, e.target.value)}
                          placeholder={placeholder.example || `Enter value for ${placeholder.name}`}
                          className="font-mono text-sm"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {editingPlaceholders && !allPlaceholdersFilled && (
                  <Alert className="border-cyan-300 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-950">
                    <AlertTriangle className="h-4 w-4 text-cyan-600" />
                    <AlertDescription className="text-xs text-cyan-800 dark:text-cyan-200">
                      All placeholder values must be filled in before the command can be executed.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel disabled={savingPlaceholders}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSavePlaceholders}
              disabled={savingPlaceholders || !allPlaceholdersFilled}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {savingPlaceholders ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Save & Update Command
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </div>
      </div>

      {/* Chat Interface - Right Side */}
      {chatMounted && (
        <div className={cn(
          "w-full max-h-[50vh] md:max-h-none md:w-[31%] border-t md:border-t-0 md:border-l border-border/50 bg-card/30 min-w-0 overflow-hidden",
          !showChat && "hidden"
        )}>
          <CloudIncidentChat incident={incident} />
        </div>
      )}
      </div>
    </div>
  );
}

